/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import store from '@/store';
import generateMutations, {
  UPDATE_STATE_TYPE,
} from '@/store/generate-mutations';
import RootStateType from '@/store/types';

import DictApi from '@/api/dict';
import UserPersonalInfoApi from '@/api/userPersonalInfo';
import type { OptionItem } from '@/model/OptionItem';
import type { UserVo } from '@/vo/user';
import type { SelectOptionDataType } from '@hb/finance-ui/src/types/shared';
import { ActionContext, Module } from 'vuex';
import { CONTACT_SPOUSE_RELATION_CODE } from '@/views_v2/verify-credit/types';
import type { StateType } from './types';

/** 联系人表单项 */
export const ContactFormItems = ['联系人1（提高审核通过率）', '联系人2（提高审核通过率）'];

/** 将{name, code}转换成{value, label}格式 */
const transformOptions = (options: OptionItem[]): SelectOptionDataType[] => options.map((item) => ({
  value: item.code,
  label: item.name,
}));

/** 创建联系人辅助信息 */
const createContactItem = (item = {
  contactName: '',
  contactPhone: '',
}) => ({
  defalutContactName: item.contactName || '',
  shouldMaskContactName: !!item.contactName,
  defalutContactPhone: item.contactPhone || '',
  shouldMaskContactPhone: !!item.contactPhone,
});
/** 创建联系人表单字段 */
const createUserEmergencyContacts = (requireSpouse?: boolean): UserVo.ContactInfo => ({
  contactName: '',
  contactNo: undefined,
  contactPhone: '',
  /** 第一联系人，已婚条件下限制成“配偶” */
  contactRelation: requireSpouse ? CONTACT_SPOUSE_RELATION_CODE : undefined,
});
/** 创建隐藏的下拉选项，当字典接口异常时，用来占位展示 */
const createHideSelectOption = () => ({
  value: '',
  label: '',
  className: 'selectListHideOption',
});

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    formData: null,
    contactRelations: [],
    originContactList: ContactFormItems.map(() => createContactItem()),
    maritalStatus: null,
  },
  actions: {
    // 初始化表单数据
    async initFormData(
      context: ActionContext<StateType, RootStateType>,
      params: {
        requireSpouseInfo: boolean
      },
    ): Promise<void> {
      try {
        const { requireSpouseInfo } = params;
        const { formData = Object.create(null) } = context.state;

        /** 获取用户信息 & 附加的信息 */
        const [personalInfo, supplyInfo] = await Promise.all([
          UserPersonalInfoApi.getZYQ(), UserPersonalInfoApi.getPersonalInfo(),
        ]);

        if (personalInfo) {
          const { userEmergencyContacts } = personalInfo;
          const userEmergencyContactList = [];
          const originContactList = [];

          /** 初始化serEmergencyContact数据，优先读取服务数据 */
          if (userEmergencyContacts && userEmergencyContacts.length) {
            userEmergencyContactList.push(...userEmergencyContacts);

            originContactList.push(...userEmergencyContacts.map((it) => createContactItem(it)));
          } else {
            userEmergencyContactList.push({ ...createUserEmergencyContacts(requireSpouseInfo) });
            userEmergencyContactList.push({ ...createUserEmergencyContacts() });

            originContactList.push(...ContactFormItems.map(() => createContactItem()));
          }

          context.dispatch(UPDATE_STATE_TYPE, {
            formData: {
              ...formData,
              userEmergencyContacts: userEmergencyContactList,
            },
            originContactList,
            maritalStatus: supplyInfo.maritalStatus,
          });
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.resolve();
      }
    },

    // 初始化下拉列表数据字典
    async initSelectOptions(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const contactRelations = await DictApi.getContactRelations();

        context.dispatch(UPDATE_STATE_TYPE, {
          contactRelations: contactRelations.length
            ? transformOptions(contactRelations) : [createHideSelectOption()],
        });

        return Promise.resolve();
      } catch (error) {
        context.dispatch(UPDATE_STATE_TYPE, {
          contactRelations: [createHideSelectOption()],
        });
        return Promise.resolve();
      }
    },
  },
};

store.registerModule(
  'personalContacts',
  generateMutations<StateType>(moduleStore),
  {
    preserveState: false,
  },
);
